import { CommandExecutionContext } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import { SimpleDocCommand } from "./types";
import { readAll as readAllDoc } from "../../services/doc/docApi";
import { generateNodeId } from '../../services/doc/docApi';
import { getDocContext as getDocCtx } from '../../services/doc/docApi';
import { replaceWithMarkdown as replaceMarkdown } from '../../services/doc/docApi';
import { traceLog } from '../../../../services/debug/traceHelpers';

export const docReplaceV2Command: SimpleDocCommand = {
  id: 'doc_replace_v2',
  description: `🔄 REPLACE: Replace specific document nodes with new Markdown content.

**SUPPORTED ENTITIES:**
- Documents (doc_*)
- Use entity_write for Notes (note_*), Sheets (sheet_*), Canvas (canvas_*), Presentations (presentation_*)
`,
  logicalName: 'doc_replace',
  version: 'v2',
  appType: 'docs',
  
  schema: {
    parameters: [
      {
        name: 'id',
        type: 'string',
        description: 'Document entity ID (llmId)',
        required: true,
      },
      {
        name: 'replaceNodes',
        type: 'array',
        description: 'Array of exact node IDs to replace with new content',
        required: true,
      },
      {
        name: 'markdownContent',
        type: 'string',
        description: 'New content in Markdown format',
        required: true,
      },
      {
        name: 'reasoning',
        type: 'string',
        description: 'Brief explanation of why this change is being made',
        required: true,
      },
    ]
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { id, replaceNodes, markdownContent, reasoning } = params;

    const traceId = context.traceId;
    traceLog({ traceId, type: 'tool_start', data: { tool: 'doc_replace_v2', input: { id, replaceNodesCount: Array.isArray(replaceNodes) ? replaceNodes.length : 'invalid' } } });
    try { context.onProgress?.({ type: 'streaming_update', activity: 'Preparing replacement...' }); } catch {}

    try {
      if (!Array.isArray(replaceNodes)) {
        const err = createErrorResult('Invalid replaceNodes', 'replaceNodes must be an array (can be empty or ["ALL"] to indicate full-document replacement)', 'validation');
        traceLog({ traceId, type: 'tool_error', data: { tool: 'doc_replace_v2', stage: 'validate_params', error: err } });
        return err;
      }

      const { doc, error } = await getDocCtx(id, context);
      try { context.onProgress?.({ type: 'streaming_update', activity: 'Resolving document context...' }); } catch {}

      if (error || !doc) {
        const err = createErrorResult('Document not available', 'Y.js document not accessible', 'validation');
        traceLog({ traceId, type: 'tool_error', data: { tool: 'doc_replace_v2', stage: 'resolve_doc', error: err } });
        return err;
      }

      (doc as any).metadata = { ...(doc as any).metadata, traceId };

      // Remove any streaming placeholders left over from a previous incomplete operation.
      // Best-effort: a cleanup failure must not prevent the replacement from proceeding.
      try {
        const yxmlArray = doc.yxml.toArray();
        const placeholderIndices = yxmlArray
          .reduce((acc: number[], el: any, idx: number) => {
            if (el && typeof el.getAttribute === 'function') {
              if (el.getAttribute('data-streaming') === 'true' || el.getAttribute('data-streaming-failed') === 'true') {
                acc.push(idx);
              }
            }
            return acc;
          }, [])
          .sort((a: number, b: number) => b - a); // delete from end first to preserve indices

        if (placeholderIndices.length > 0) {
          doc.yDoc.transact(() => {
            placeholderIndices.forEach((idx: number) => doc.yxml.delete(idx, 1));
          }, { source: 'tool-execution-cleanup', toolUseId: context.executionId });
        }
      } catch (cleanupErr) {
        console.warn('[DOC_REPLACE_V2] Streaming placeholder cleanup failed (continuing):', cleanupErr instanceof Error ? cleanupErr.message : String(cleanupErr));
      }

      // Guardrail: encourage iterative edits by limiting large spans
      const MAX_NODES_PER_CALL = 6;
      if (replaceNodes.length > MAX_NODES_PER_CALL) {
        const err = createErrorResult(
          'Replace range too large',
          `Please target at most ${MAX_NODES_PER_CALL} contiguous nodes per call. Use multiple small passes.`,
          'validation'
        );
        traceLog({ traceId, type: 'tool_error', data: { tool: 'doc_replace_v2', stage: 'validate_span_size', error: err, requested: replaceNodes.length } });
        return err;
      }

      const docCtx = { yxml: doc.yxml, doc: doc.yDoc, docId: doc.docId, metadata: doc.metadata } as any;

      // Determine whether this is a full-document replacement or a targeted node replacement
      const isSentinel = replaceNodes.length === 0 ||
        (replaceNodes.length === 1 && ['ALL', '__ALL__', 'NODE_ROOT'].includes(String(replaceNodes[0]).toUpperCase()));

      let fromId: string;
      let toId: string;

      traceLog({ traceId, type: 'doc_replace_params', data: { replaceNodes, markdownPreview: String(markdownContent).slice(0, 80) } });
      try { context.onProgress?.({ type: 'streaming_update', activity: 'Computing target span...' }); } catch {}

      if (isSentinel) {
        const all = readAllDoc(docCtx);
        if (!all.nodes || all.nodes.length === 0) {
          // Empty document: initialise with a placeholder node then replace it
          const placeholderId = generateNodeId();
          const respInit = replaceMarkdown(docCtx, { from: placeholderId, to: placeholderId }, '', {
            batchDescription: 'init placeholder',
            restrictIds: [placeholderId],
            suggestionMode: false,
          });
          if (!respInit || (respInit as any).ok === false) {
            const err = createErrorResult('Initialize failed', 'Failed to initialize empty document for replacement', 'execution');
            traceLog({ traceId, type: 'tool_error', data: { tool: 'doc_replace_v2', stage: 'init_empty_doc', error: err } });
            return err;
          }
          fromId = placeholderId;
          toId = placeholderId;
        } else {
          fromId = all.nodes[0].id;
          toId = all.nodes[all.nodes.length - 1].id;
        }
      } else {
        // Validate that every provided node ID exists in the current document
        const all = readAllDoc(docCtx);
        const existingIds = new Set((all.nodes || []).map((n: any) => String(n.id)));
        const missingIds = replaceNodes.filter((nodeId: string) => !existingIds.has(String(nodeId)));
        if (missingIds.length > 0) {
          const err = createErrorResult(
            'Node IDs not found in document',
            `These node IDs do not exist in the current document: [${missingIds.join(', ')}]. Call entity_read_content to get the current node IDs before retrying.`,
            'validation'
          );
          traceLog({ traceId, type: 'tool_error', data: { tool: 'doc_replace_v2', stage: 'validate_node_ids', missingIds, error: err } });
          return err;
        }

        fromId = String(replaceNodes[0]);
        toId = String(replaceNodes[replaceNodes.length - 1]);
      }

      traceLog({ traceId, type: 'doc_replace_range', data: { fromId, toId, count: replaceNodes.length } });
      try { context.onProgress?.({ type: 'streaming_update', activity: 'Applying changes...' }); } catch {}

      const resp = replaceMarkdown(docCtx, { from: fromId, to: toId }, markdownContent, {
        batchDescription: reasoning,
        restrictIds: !isSentinel ? replaceNodes.map(String) : undefined,
        suggestionMode: false,
      });

      if (!resp || (resp as any).ok === false) {
        const respError = (resp as any)?.error ?? 'unknown';
        const err = createErrorResult(
          'Replace failed',
          `Failed to replace nodes with Markdown content (reason: ${respError}). Ensure the node IDs are current by calling entity_read_content first.`,
          'execution'
        );
        traceLog({ traceId, type: 'tool_error', data: { tool: 'doc_replace_v2', stage: 'execute', respError, error: err } });
        return err;
      }

      const revisionId = (resp as any).revisionId;
      const insertedIds = (resp as any).insertedIds || [];
      const newNodes = readAllDoc(docCtx).nodes.filter((n: any) => insertedIds.includes(n.id));
      const diffSummary = `Inserted ${newNodes.length} node(s); replaced ${isSentinel ? 'entire document' : `${replaceNodes.length} node(s)`}.`;

      try { context.onProgress?.({ type: 'streaming_update', activity: 'Finalizing...', text_total: diffSummary }); } catch {}
      traceLog({ traceId, type: 'tool_end', data: { tool: 'doc_replace_v2', success: true, newNodes: newNodes.length, revisionId } });

      return createSuccessResult(
        `Successfully replaced ${isSentinel ? 'entire document' : `${replaceNodes.length} node(s)`} with new Markdown content.`,
        {
          type: 'document_replace',
          replacedCount: isSentinel ? newNodes.length : replaceNodes.length,
          newNodes,
          revisionId,
          diffSummary,
        },
        {
          format: 'json',
          metadata: { executionTime: Date.now() - startTime, affectedEntities: [doc.windowId || doc.docId] },
        }
      );

    } catch (error) {
      const err = createErrorResult('Replace operation failed', error instanceof Error ? error.message : 'Unknown error', 'execution');
      traceLog({ traceId, type: 'tool_error', data: { tool: 'doc_replace_v2', stage: 'catch', error: String(error) } });
      return err;
    }
  }
};
